export const MAX_TEXT_PANELS = 6;
export const MAX_IMAGE_PANELS = 3;

export type ModelInfo = {
  id: string;
  name: string;
  created: number;
  contextLength: number | null;
  promptPrice: string | null;
  completionPrice: string | null;
};

export type ModelsResponse = {
  models: ModelInfo[];
  source: "live" | "fallback";
};

// Tried in order against the live catalog to pre-fill the text panels.
export const preferredTextModels = [
  "anthropic/claude-sonnet-5",
  "openai/gpt-5.6-luna",
  "google/gemini-3.5-flash",
  "x-ai/grok-4.5",
  "anthropic/claude-haiku-4.5",
  "deepseek/deepseek-chat-v3.1",
  "openai/gpt-5.4-mini",
  "meta-llama/llama-4-maverick",
];

export const pickDefaultTextModels = (
  available: ModelInfo[],
  count: number
): string[] => {
  const ids = new Set(available.map((model) => model.id));
  const picked = preferredTextModels.filter((id) => ids.has(id)).slice(0, count);

  for (const model of available) {
    if (picked.length >= count) break;
    if (!picked.includes(model.id)) picked.push(model.id);
  }

  return picked;
};

export type FalModelOption = {
  id: string;
  label: string;
};

// Curated fal.ai text-to-image endpoints (all verified against the fal
// queue); any other model id can also be typed into the picker.
export const falModelOptions: FalModelOption[] = [
  { id: "fal-ai/flux/schnell", label: "FLUX.1 Schnell (fast, cheap)" },
  { id: "fal-ai/nano-banana", label: "Nano Banana (Gemini Flash Image)" },
  { id: "fal-ai/z-image/turbo", label: "Z-Image Turbo (very cheap)" },
  { id: "fal-ai/qwen-image", label: "Qwen Image" },
  { id: "fal-ai/hidream-i1-fast", label: "HiDream-I1 Fast" },
  { id: "fal-ai/flux/dev", label: "FLUX.1 Dev" },
  { id: "fal-ai/bytedance/seedream/v4/text-to-image", label: "Seedream 4.0" },
  { id: "fal-ai/imagen4/preview", label: "Google Imagen 4" },
  { id: "fal-ai/ideogram/v3", label: "Ideogram 3" },
  { id: "fal-ai/recraft/v3/text-to-image", label: "Recraft V3" },
  { id: "fal-ai/nano-banana-pro", label: "Nano Banana Pro" },
  { id: "fal-ai/flux-pro/v1.1-ultra", label: "FLUX 1.1 Pro Ultra" },
];

export const defaultImageModels = [
  "fal-ai/flux/schnell",
  "fal-ai/nano-banana",
  "fal-ai/z-image/turbo",
];

export type GeneratedImage = {
  url: string;
  width: number | null;
  height: number | null;
  contentType: string | null;
};

export type ImageGenerationResult = {
  requestId: string;
  model: string;
  images: GeneratedImage[];
  durationMs: number;
  mocked: boolean;
  notice?: string;
};

export const formatDuration = (durationMs: number) =>
  durationMs >= 1000
    ? `${(durationMs / 1000).toFixed(1)}s`
    : `${Math.round(durationMs)}ms`;
